import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, CheckCircle } from 'lucide-react';

const LOGO_URL = "/images/area-logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      if (response.ok) {
        setSuccess(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="reset-password-page">
        <div className="w-full max-w-md text-center">
          <Link to="/"><img src={LOGO_URL} alt="AREA" className="h-14 w-auto mx-auto mb-6" /></Link>
          <Card className="border border-slate-200 shadow-lg">
            <CardContent className="p-8">
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Invalid reset link</h2>
              <p className="text-sm text-slate-600 mb-6">This password reset link is invalid or has expired.</p>
              <Link to="/forgot-password">
                <Button className="w-full bg-[#3D4F7A] hover:bg-[#2A3755]">Request a new link</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/"><img src={LOGO_URL} alt="AREA" className="h-14 w-auto mx-auto mb-4" /></Link>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {success ? 'Password reset!' : 'Set a new password'}
          </h1>
        </div>

        <Card className="border border-slate-200 shadow-lg">
          <CardContent className="p-8">
            {success ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-sm text-slate-600 mb-6">
                  Your password has been reset successfully. You can now sign in with your new password.
                </p>
                <Link to="/login">
                  <Button className="w-full bg-[#3D4F7A] hover:bg-[#2A3755]" data-testid="go-to-login-btn">
                    Go to sign in
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min 8 characters" required className="pl-9 h-11" data-testid="reset-password-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirm new password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password" required className="pl-9 h-11" data-testid="reset-confirm-input" />
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 bg-[#3D4F7A] hover:bg-[#2A3755]" disabled={loading} data-testid="reset-submit">
                  {loading ? 'Resetting...' : 'Reset password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
